import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { App, Button, Card, Empty, Input, Spin, Typography, Upload } from "antd";
import {
  ArrowRightOutlined,
  PaperClipOutlined,
  ReloadOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  filterAgentOperationRequests,
  STATUS_FILTER_ALL,
  formatDateTime,
  statusLabelFromItem,
} from "../../../features/operationRequest/agentOperationRequestListUtils.js";
import { QUICK_REQUEST_TYPE_BLOCKS } from "../../../features/operationRequest/operationRequestConstants.js";
import OperationRequestHandlingTimer from "../../../features/operationRequest/components/OperationRequestHandlingTimer.jsx";
import {
  buildOperationRequestAttachmentUrl,
  createAdminOperationRequest,
  createOperationRequest,
  fetchAdminOperationRequestDetail,
  fetchAdminOperationRequests,
  fetchMyOperationRequestDetail,
  fetchMyOperationRequests,
  filesToOperationRequestAttachments,
  markAdminOperationRequestRead,
  markAgentOperationRequestRead,
  postAdminOperationRequestMessage,
  postAdminOperationRequestStatus,
  postAgentOperationRequestMessage,
} from "../../../features/operationRequest/operationRequestsApi.js";
import { resolvePickerOrderDisplayNumber } from "../../../features/operationRequest/pickerOrderDisplayResolver.js";
import { isAdminUser } from "../../../services/authService";
import { buildPickerOrderFromLinkedBooking } from "./crmLinkedBooking";
import { formatHrBookingRef } from "../../../utils/bookingRefDisplay.js";

const { Text } = Typography;

const VIEW_TYPES = "types";
const VIEW_CHAT = "chat";

/** Quiet refresh while Ops chat is open (no websocket yet). */
const CHAT_POLL_MS = 2500;

const OPENISH_STATUSES = new Set([
  "open",
  "in_progress",
  "waiting_for_payment",
  "waiting_payment",
  "paid",
]);

const TERMINAL_STATUSES = new Set([
  "completed",
  "rejected",
  "cancelled",
  "closed",
]);

function isAdminMessage(msg) {
  return Boolean(
    msg?.isAdmin ||
      msg?.is_admin ||
      msg?.senderType === "admin" ||
      msg?.sender_type === "admin"
  );
}

function isSystemMessage(msg) {
  return Boolean(
    msg?.isSystem ||
      msg?.is_system ||
      msg?.senderType === "system" ||
      msg?.sender_type === "system"
  );
}

function requestTypeOf(item) {
  return String(item?.requestType || item?.request_type || "").trim();
}

function requestTypeLabel(value) {
  const hit = QUICK_REQUEST_TYPE_BLOCKS.find((b) => b.value === value);
  return hit?.label || value || "פנייה";
}

/** Prefer an open request of the same type; else null. */
function pickOpenRequestForType(items, typeValue) {
  const type = String(typeValue || "").trim();
  const list = Array.isArray(items) ? items : [];
  return (
    list.find(
      (item) =>
        requestTypeOf(item) === type &&
        OPENISH_STATUSES.has(String(item?.status || "").trim().toLowerCase())
    ) || null
  );
}

function isTerminalRequest(item) {
  return TERMINAL_STATUSES.has(String(item?.status || "").trim().toLowerCase());
}

/** Most recent thread of this type (open preferred, else any including closed). */
function pickLatestRequestForType(items, typeValue) {
  const type = String(typeValue || "").trim();
  const list = Array.isArray(items) ? items : [];
  const ofType = list.filter((item) => requestTypeOf(item) === type);
  const open = ofType.find((item) =>
    OPENISH_STATUSES.has(String(item?.status || "").trim().toLowerCase())
  );
  if (open) return open;
  return ofType[0] || null;
}

function typeCardHasUnread(items, typeValue) {
  const open = pickOpenRequestForType(items, typeValue);
  if (!open) return false;
  return Boolean(open.hasUnreadAdminReply || open.has_unread_admin_reply);
}

function typeCardStatusLabel(items, typeValue) {
  const open = pickOpenRequestForType(items, typeValue);
  if (open && typeCardHasUnread(items, typeValue)) return "תשובה חדשה מאופרציה";
  if (open) return "שיחה פתוחה";
  const latest = pickLatestRequestForType(items, typeValue);
  if (latest && isTerminalRequest(latest)) return "השיחה הסתיימה";
  return null;
}

/**
 * CRM left-pane Ops channel:
 * 1) Keep the request-type buttons
 * 2) Clicking a type opens the Ops chat for that type
 */
export default function CrmEmbeddedOpsPane({
  linkedBooking,
  initialOpsType = null,
  initialOpsRequestId = null,
  leadAgentId = null,
}) {
  const { message } = App.useApp();
  const threadRef = useRef(null);
  const initialTypeOpenedRef = useRef("");
  const initialOpsRequestOpenedRef = useRef("");
  const isAdmin = isAdminUser();

  const [view, setView] = useState(VIEW_TYPES);
  const [bootLoading, setBootLoading] = useState(true);
  const [listItems, setListItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState("");
  const [fileList, setFileList] = useState([]);
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [chatRequestType, setChatRequestType] = useState(null);

  const selectedOrder = useMemo(
    () => buildPickerOrderFromLinkedBooking(linkedBooking),
    [linkedBooking]
  );
  const orderId = String(selectedOrder?.id || linkedBooking?.order_id || "").trim();
  const opsRequestId = String(
    initialOpsRequestId ||
      linkedBooking?.ops_request_id ||
      linkedBooking?.opsRequestId ||
      ""
  ).trim();
  const isOpsOnlyDocket = Boolean(opsRequestId) && !orderId;
  const bookingLabel =
    formatHrBookingRef(linkedBooking?.booking_ref) ||
    linkedBooking?.booking_ref ||
    (isOpsOnlyDocket ? "פנייה כללית" : "") ||
    orderId ||
    "";

  const orderRequests = useMemo(() => {
    if (isOpsOnlyDocket) {
      const list = Array.isArray(listItems) ? listItems : [];
      return list.filter((item) => String(item?.id || "").trim() === opsRequestId);
    }
    return filterAgentOperationRequests(listItems, {
      statusFilter: STATUS_FILTER_ALL,
      searchQuery: "",
      orderId,
    });
  }, [isOpsOnlyDocket, listItems, opsRequestId, orderId]);

  const messages = useMemo(
    () => (Array.isArray(detail?.messages) ? detail.messages : []),
    [detail]
  );

  const agentSenderLabel = useMemo(() => {
    const name = String(detail?.agentName || detail?.agent_name || "").trim();
    const phone = String(detail?.agentPhone || detail?.agent_phone || "").trim();
    if (name && phone) return `${name} / ${phone}`;
    if (name) return name;
    if (phone) return phone;
    const agentId = detail?.agentId ?? detail?.agent_id;
    return agentId != null && String(agentId).trim()
      ? `סוכן #${String(agentId).trim()}`
      : "סוכן";
  }, [detail]);

  const scrollThreadToEnd = useCallback(() => {
    const el = threadRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    scrollThreadToEnd();
  }, [messages.length, scrollThreadToEnd]);

  const loadDetail = useCallback(
    async (id, { markRead = true, quiet = false } = {}) => {
      const rid = String(id || "").trim();
      if (!rid) {
        setDetail(null);
        setDetailId(null);
        return null;
      }
      if (!quiet) {
        setDetailLoading(true);
        setError("");
      }
      try {
        const data = isAdmin
          ? await fetchAdminOperationRequestDetail(rid)
          : await fetchMyOperationRequestDetail(rid);
        if (!data?.ok) throw new Error(data?.message || "טעינה נכשלה");
        setDetail(data.request);
        setDetailId(rid);
        if (markRead) {
          // Dual-role admins: clearing only the admin cursor left the agent bell
          // stuck after they already read the thread in the admin Ops UI.
          if (isAdmin) {
            try {
              await markAdminOperationRequestRead(rid);
            } catch {
              /* best-effort */
            }
          }
          try {
            await markAgentOperationRequestRead(rid);
          } catch {
            /* not the owning agent / no agent session — ignore */
          }
        }
        return data.request;
      } catch (e) {
        if (!quiet) {
          setDetail(null);
          setError(String(e?.response?.data?.message || e?.message || e));
        }
        return null;
      } finally {
        if (!quiet) setDetailLoading(false);
      }
    },
    [isAdmin]
  );

  const refreshList = useCallback(async () => {
    if (!orderId && !opsRequestId) {
      setBootLoading(false);
      setListItems([]);
      return [];
    }
    setBootLoading(true);
    setError("");
    try {
      const data = isAdmin
        ? await fetchAdminOperationRequests()
        : await fetchMyOperationRequests();
      let items = Array.isArray(data?.items) ? data.items : [];

      // Ops-only docket: ensure the linked request is in the list (detail fetch fallback).
      if (opsRequestId && !orderId) {
        const has = items.some((item) => String(item?.id || "").trim() === opsRequestId);
        if (!has) {
          try {
            const one = isAdmin
              ? await fetchAdminOperationRequestDetail(opsRequestId)
              : await fetchMyOperationRequestDetail(opsRequestId);
            if (one?.ok && one?.request) {
              items = [one.request, ...items];
            }
          } catch {
            /* keep list as-is */
          }
        }
        setListItems(items);
        return items.filter((item) => String(item?.id || "").trim() === opsRequestId);
      }

      setListItems(items);
      return filterAgentOperationRequests(items, {
        statusFilter: STATUS_FILTER_ALL,
        searchQuery: "",
        orderId,
      });
    } catch (e) {
      setListItems([]);
      setError(String(e?.message || e));
      return [];
    } finally {
      setBootLoading(false);
    }
  }, [isAdmin, orderId, opsRequestId]);

  const openChatForType = useCallback(
    async (typeValue, { preferNew = false } = {}) => {
      const type = String(typeValue || "").trim();
      if (!type) return;
      setChatRequestType(type);
      setDraft("");
      setError("");
      setView(VIEW_CHAT);

      const filtered =
        orderRequests.length > 0 ? orderRequests : await refreshList();
      const open = pickOpenRequestForType(filtered, type);
      if (open?.id && !preferNew) {
        await loadDetail(open.id);
        return;
      }
      if (preferNew) {
        setDetail(null);
        setDetailId(null);
        return;
      }
      // Review latest closed thread if exists; otherwise empty new chat.
      const latest = pickLatestRequestForType(filtered, type);
      if (latest?.id) {
        await loadDetail(latest.id);
      } else {
        setDetail(null);
        setDetailId(null);
      }
    },
    [loadDetail, orderRequests, refreshList]
  );

  const startNewChat = useCallback(() => {
    const type =
      chatRequestType ||
      detail?.requestType ||
      detail?.request_type ||
      "general_inquiry";
    setChatRequestType(String(type).trim());
    setDetail(null);
    setDetailId(null);
    setDraft("");
    setError("");
    setView(VIEW_CHAT);
    message.info("פנייה חדשה — כתבו הודעה כדי לפתוח מול האופרציה");
  }, [chatRequestType, detail, message]);

  const bookingIdentity = opsRequestId || orderId;

  useEffect(() => {
    void refreshList();
  }, [bookingIdentity, refreshList]);

  useEffect(() => {
    setView(VIEW_TYPES);
    setDetail(null);
    setDetailId(null);
    setDraft("");
    setChatRequestType(null);
    initialTypeOpenedRef.current = "";
    initialOpsRequestOpenedRef.current = "";
  }, [bookingIdentity]);

  /** Deep-link: open this ops request thread (with or without a linked order). */
  useEffect(() => {
    if (!opsRequestId || bootLoading) return;
    if (initialOpsRequestOpenedRef.current === opsRequestId) return;
    initialOpsRequestOpenedRef.current = opsRequestId;
    const type =
      String(initialOpsType || "").trim() ||
      String(linkedBooking?.request_type || "").trim() ||
      "general_inquiry";
    setChatRequestType(type);
    setView(VIEW_CHAT);
    void loadDetail(opsRequestId);
  }, [
    bootLoading,
    initialOpsType,
    linkedBooking?.request_type,
    loadDetail,
    opsRequestId,
  ]);

  /** Deep-link from bell / admin inbox: open the requested type chat immediately. */
  useEffect(() => {
    const type = String(initialOpsType || "").trim();
    if (!type || !orderId || bootLoading || opsRequestId) return;
    if (initialTypeOpenedRef.current === `${orderId}:${type}`) return;
    initialTypeOpenedRef.current = `${orderId}:${type}`;
    void openChatForType(type);
  }, [bootLoading, initialOpsType, openChatForType, opsRequestId, orderId]);

  const backToTypes = useCallback(() => {
    setView(VIEW_TYPES);
    setDetail(null);
    setDetailId(null);
    setDraft("");
    setChatRequestType(null);
    setError("");
    void refreshList();
  }, [refreshList]);

  const requestClosed = useMemo(() => {
    const st = String(detail?.status || "").trim().toLowerCase();
    return Boolean(st && TERMINAL_STATUSES.has(st));
  }, [detail?.status]);

  /** Live-ish updates: poll open chat + refresh on tab focus / unread event. */
  useEffect(() => {
    if (view !== VIEW_CHAT || !detailId || requestClosed) return undefined;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.hidden) return;
      void loadDetail(detailId, { markRead: true, quiet: true });
    };
    const intervalId = window.setInterval(tick, CHAT_POLL_MS);
    const onVisible = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        tick();
      }
    };
    const onUnread = () => tick();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    window.addEventListener("operationRequestUnreadChanged", onUnread);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("operationRequestUnreadChanged", onUnread);
    };
  }, [view, detailId, requestClosed, loadDetail]);

  const handleCloseRequest = useCallback(async () => {
    if (!isAdmin || !detailId || requestClosed) return;
    setClosing(true);
    try {
      const data = await postAdminOperationRequestStatus(detailId, "completed");
      if (!data?.ok) throw new Error(data?.message || "סגירה נכשלה");
      message.success("הפנייה נסגרה — נשלח מייל לסוכן עם ההתכתבות");
      if (data.request) setDetail(data.request);
      else await loadDetail(detailId);
      void refreshList();
    } catch (e) {
      message.error(String(e?.response?.data?.message || e?.message || e));
    } finally {
      setClosing(false);
    }
  }, [detailId, isAdmin, loadDetail, message, refreshList, requestClosed]);

  const handleSend = useCallback(async () => {
    const text = String(draft || "").trim();
    if (!text && !fileList.length) {
      message.warning("נא להזין הודעה או לצרף קובץ");
      return;
    }
    if (!orderId && !opsRequestId && !detailId) {
      message.warning("אין הזמנה או פנייה מקושרת");
      return;
    }
    if (requestClosed) {
      message.warning("הפנייה נסגרה — פתחו שיחה חדשה כדי להמשיך");
      return;
    }
    const type =
      chatRequestType ||
      linkedBooking?.request_type ||
      "general_inquiry";

    setSending(true);
    setError("");
    try {
      const { attachments, error: attError } =
        await filesToOperationRequestAttachments(fileList);
      if (attError) {
        message.error(attError);
        return;
      }
      const attPayload = attachments.length ? attachments : null;

      if (detailId) {
        const data = isAdmin
          ? await postAdminOperationRequestMessage(detailId, text, attPayload)
          : await postAgentOperationRequestMessage(detailId, text, attPayload);
        if (!data?.ok) throw new Error(data?.message || "שליחה נכשלה");
        setDraft("");
        setFileList([]);
        if (data.request) {
          setDetail(data.request);
        } else if (data.message) {
          setDetail((prev) => {
            if (!prev) return prev;
            const existing = Array.isArray(prev.messages) ? prev.messages : [];
            return {
              ...prev,
              messages: [...existing, data.message],
              latestMessagePreview: data.message.message ?? prev.latestMessagePreview,
            };
          });
        } else {
          await loadDetail(detailId);
        }
        message.success(
          isAdmin
            ? "התגובה נשלחה — הסוכן יקבל מייל והתראה בדשבורד"
            : "ההודעה נשלחה לאופרציה"
        );
        return;
      }

      if (attPayload?.length && !text) {
        message.warning("לפתיחת שיחה חדשה יש לכתוב הודעה (אפשר לצרף קבצים אחרי הפתיחה)");
        return;
      }

      const display = orderId
        ? resolvePickerOrderDisplayNumber(selectedOrder)
        : { displayNumber: "כללי" };
      const agentIdFromThread =
        detail?.agentId ??
        detail?.agent_id ??
        leadAgentId ??
        orderRequests.find((r) => requestTypeOf(r) === type)?.agentId ??
        orderRequests.find((r) => requestTypeOf(r) === type)?.agent_id ??
        orderRequests[0]?.agentId ??
        orderRequests[0]?.agent_id ??
        null;

      let data;
      if (isAdmin) {
        const aid = Number(agentIdFromThread || 0);
        if (!aid) {
          message.warning("לא נמצא סוכן לפתיחת שיחה — פתחו פנייה קודמת או עברו מדוקט של הסוכן");
          return;
        }
        data = await createAdminOperationRequest({
          agent_id: aid,
          order_id: orderId || null,
          display_order_number: display.displayNumber || null,
          order_type: orderId
            ? selectedOrder?.rawOrderType || selectedOrder?.orderType || null
            : null,
          order_category: orderId ? selectedOrder?.category || null : null,
          request_type: type,
          message: text,
          payment_required: false,
        });
      } else {
        data = await createOperationRequest({
          order_id: orderId || null,
          display_order_number: display.displayNumber || null,
          order_type: orderId
            ? selectedOrder?.rawOrderType || selectedOrder?.orderType || null
            : null,
          order_category: orderId ? selectedOrder?.category || null : null,
          request_type: type,
          message: text,
          payment_required: false,
        });
      }
      if (!data?.ok || !data?.request) {
        throw new Error(data?.message || data?.error || "יצירת פנייה נכשלה");
      }
      setDraft("");
      const created = data.request;
      const id = String(created.id || "").trim();
      setListItems((prev) => [created, ...(Array.isArray(prev) ? prev : [])]);
      if (id) {
        await loadDetail(id);
      } else {
        setDetail(created);
      }
      if (created.appendedToExisting) {
        message.success(
          isAdmin ? "ההודעה נוספה לשיחה — הסוכן יראה אותה בדשבורד" : "ההודעה נוספה לשיחה הפתוחה"
        );
      } else if (isAdmin) {
        message.success(
          created.agentEmailNotified
            ? "פנייה נפתחה לסוכן — נשלח מייל והתראה בדשבורד"
            : "פנייה נפתחה לסוכן — התראה בדשבורד (מייל לא נשלח)"
        );
      } else {
        message.success("שיחה חדשה נפתחה");
      }
    } catch (e) {
      message.error(String(e?.response?.data?.message || e?.message || e));
    } finally {
      setSending(false);
    }
  }, [
    chatRequestType,
    detail,
    detailId,
    draft,
    fileList,
    isAdmin,
    leadAgentId,
    linkedBooking?.request_type,
    loadDetail,
    message,
    opsRequestId,
    orderId,
    orderRequests,
    requestClosed,
    selectedOrder,
  ]);

  if (!orderId && !opsRequestId) {
    return (
      <div className="crm-embedded-ops" dir="rtl">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="פניות אופרציה זמינות כשיש הזמנה או פנייה כללית מקושרת"
        />
      </div>
    );
  }

  if (view === VIEW_TYPES) {
    return (
      <div className="crm-embedded-ops" dir="rtl">
        <div className="crm-embedded-ops__toolbar">
          <Text strong>בחר סוג פנייה</Text>
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined />}
            loading={bootLoading}
            onClick={() => void refreshList()}
            aria-label="רענון"
          />
        </div>
        <p className="crm-embedded-ops__hint">
          {isOpsOnlyDocket ? "פנייה כללית" : `הזמנה ${bookingLabel}`} —{" "}
          {isAdmin
            ? "לחיצה פותחת פנייה לסוכן (מייל + התראה בדשבורד)"
            : "לחיצה נכנסת לצ׳אט עם האופרציה"}
        </p>
        {error ? <Text type="danger">{error}</Text> : null}
        {bootLoading ? (
          <div className="crm-embedded-ops__loading">
            <Spin />
          </div>
        ) : (
          <div className="crm-embedded-ops__type-grid">
            {QUICK_REQUEST_TYPE_BLOCKS.map((b) => {
              const statusLabel = typeCardStatusLabel(orderRequests, b.value);
              const unread = typeCardHasUnread(orderRequests, b.value);
              return (
                <Card
                  key={b.value}
                  size="small"
                  role="button"
                  tabIndex={0}
                  className={`crm-embedded-ops__type-card${
                    unread ? " crm-embedded-ops__type-card--unread" : ""
                  }`}
                  onClick={() => void openChatForType(b.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      void openChatForType(b.value);
                    }
                  }}
                >
                  <Text strong style={{ display: "block", textAlign: "center" }}>
                    {b.label}
                  </Text>
                  {statusLabel ? (
                    <Text
                      type={unread ? "danger" : "secondary"}
                      style={{ display: "block", textAlign: "center", fontSize: 11, marginTop: 4 }}
                    >
                      {statusLabel}
                    </Text>
                  ) : null}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="crm-embedded-ops crm-embedded-ops--chat" dir="rtl">
      <div className="crm-embedded-ops__chat-head">
        <div className="crm-embedded-ops__chat-titles">
          <Button
            type="link"
            icon={<ArrowRightOutlined />}
            onClick={backToTypes}
            style={{ paddingInline: 0, height: "auto" }}
          >
            חזרה לסוגי פנייה
          </Button>
          <Text strong>
            {requestTypeLabel(chatRequestType || detail?.requestType || detail?.request_type)}
          </Text>
          <Text type="secondary" className="crm-embedded-ops__chat-sub">
            {bookingLabel}
            {detail ? ` · ${statusLabelFromItem(detail) || detail.status || ""}` : " · שיחה חדשה"}
            {isAdmin && agentSenderLabel ? ` · סוכן: ${agentSenderLabel}` : ""}
          </Text>
          {detail ? <OperationRequestHandlingTimer request={detail} compact /> : null}
        </div>
        <div className="crm-embedded-ops__chat-tools">
          {isAdmin && detailId && !requestClosed ? (
            <Button
              type="primary"
              size="small"
              danger
              loading={closing}
              onClick={() => void handleCloseRequest()}
            >
              סגור פנייה
            </Button>
          ) : null}
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined />}
            loading={detailLoading}
            onClick={() => {
              if (detailId) void loadDetail(detailId);
            }}
            aria-label="רענון"
          />
        </div>
      </div>

      {requestClosed ? (
        <div className="crm-embedded-ops__closed-banner">
          <div>
            הפנייה נסגרה על ידי האופרציה. סיכום ההתכתבות נשלח למייל הסוכן.
          </div>
          <Button
            type="primary"
            size="small"
            className="crm-embedded-ops__new-request-btn"
            style={{ marginTop: 8 }}
            onClick={startNewChat}
          >
            פתח פנייה חדשה
          </Button>
        </div>
      ) : (
        <div className="crm-embedded-ops__open-banner">
          הפנייה נשארת פתוחה עד שאופרציה סוגרת אותה.
        </div>
      )}

      {error ? (
        <Text type="danger" style={{ padding: "0 12px" }}>
          {error}
        </Text>
      ) : null}

      <div
        ref={threadRef}
        className="crm-imsg-thread crm-lead-detail__thread crm-embedded-ops__thread"
      >
        {detailLoading ? (
          <div className="crm-lead-detail__thread-loading">
            <Spin />
          </div>
        ) : messages.length ? (
          messages.map((msg) => {
            const key = String(
              msg?.id || `${msg?.createdAt || msg?.created_at}-${msg?.message?.slice?.(0, 12)}`
            );
            const adminMsg = isAdminMessage(msg);
            const system = isSystemMessage(msg);
            const mine = isAdmin ? adminMsg : !adminMsg && !system;
            const rowClass = system
              ? "crm-imsg-row crm-imsg-row--system"
              : mine
                ? "crm-imsg-row"
                : "crm-imsg-row crm-imsg-row--other";
            const bubbleClass = system
              ? "crm-imsg-bubble crm-imsg-bubble--system"
              : mine
                ? "crm-imsg-bubble crm-imsg-bubble--mine"
                : "crm-imsg-bubble crm-imsg-bubble--other";
            return (
              <div key={key} className={rowClass}>
                <div className={bubbleClass}>
                  <div>{msg?.message || "—"}</div>
                  {Array.isArray(msg?.attachments) && msg.attachments.length ? (
                    <div className="crm-embedded-ops__attachments">
                      {msg.attachments.map((att) => {
                        const aid = String(att?.id || "").trim();
                        const name = att?.filename || att?.name || "קובץ";
                        const href = buildOperationRequestAttachmentUrl(
                          detailId || msg?.requestId || msg?.request_id,
                          aid,
                          { isAdmin }
                        );
                        return (
                          <a
                            key={aid || name}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="crm-embedded-ops__attachment-link"
                          >
                            <PaperClipOutlined aria-hidden /> {name}
                          </a>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
                <div className="crm-imsg-meta">
                  {system
                    ? "מערכת"
                    : adminMsg
                      ? "צוות אופרציה"
                      : isAdmin
                        ? agentSenderLabel
                        : "אני"}
                  {" · "}
                  {formatDateTime(msg?.createdAt ?? msg?.created_at) || "—"}
                </div>
              </div>
            );
          })
        ) : (
          <div className="crm-imsg-empty">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                isAdmin
                  ? "אין הודעות עדיין — הסוכן צריך לפתוח את השיחה"
                  : `כתבו הודעה בנושא «${requestTypeLabel(chatRequestType)}» — תישלח לאופרציה`
              }
            />
          </div>
        )}
      </div>

      {requestClosed ? (
        <div className="crm-notes-composer crm-lead-detail__composer crm-embedded-ops__closed-composer">
          <Button
            type="primary"
            block
            size="large"
            className="crm-embedded-ops__new-request-btn"
            onClick={startNewChat}
          >
            פתח פנייה חדשה
          </Button>
        </div>
      ) : (
        <div className="crm-notes-composer crm-lead-detail__composer">
          <Input.TextArea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            placeholder={
              detailId
                ? isAdmin
                  ? "כתבו תגובה לסוכן..."
                  : "כתבו הודעה לאופרציה..."
                : isAdmin
                  ? "כתבו הודעה כדי לפתוח שיחה חדשה מול הסוכן..."
                  : "כתבו הודעה כדי לפתוח שיחה חדשה מול האופרציה..."
            }
            maxLength={4000}
            disabled={sending}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                void handleSend();
              }
            }}
          />
          <div className="crm-notes-composer__actions">
            <Upload
              multiple
              fileList={fileList}
              beforeUpload={() => false}
              onChange={({ fileList: next }) => setFileList(next.slice(0, 5))}
              disabled={sending || !detailId}
              showUploadList={Boolean(fileList.length)}
            >
              <Button
                icon={<UploadOutlined />}
                disabled={sending || !detailId}
                size="small"
                title={
                  detailId
                    ? "העלאת קבצים"
                    : "ניתן לצרף קבצים אחרי פתיחת השיחה"
                }
              >
                קבצים
              </Button>
            </Upload>
            <span className="crm-embedded-ops__composer-hint">
              Enter לשליחה · Shift+Enter לשורה חדשה
            </span>
            <Button
              className="crm-notes-send-btn"
              type="primary"
              loading={sending}
              disabled={!String(draft || "").trim() && !fileList.length}
              onClick={() => void handleSend()}
              icon={fileList.length ? <PaperClipOutlined /> : undefined}
            >
              {detailId ? "שלח הודעה" : "פתח שיחה ושלח"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
